precision highp float;

uniform float uTime;
uniform vec2 uResolution;
uniform float uBass;
uniform float uMid;
uniform float uHigh;
uniform float uAudioAverage;
uniform float uZoom;
uniform vec2 uOffset;
uniform vec3 uColorPrimary;
uniform vec3 uColorSecondary;
uniform vec3 uColorAccent;
uniform vec2 uDeviceTilt;

varying vec2 vUv;

const int MAX_ITERATIONS = 200;
const float PI = 3.14159265359;

mat2 rotate2D(float a) {
  float c = cos(a);
  float s = sin(a);
  return mat2(c, -s, s, c);
}

void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution.xy) / min(uResolution.x, uResolution.y);
  
  // The synthesis - all previous themes combine
  // Cyclical nature - returning to the beginning
  
  float cycle = sin(uTime * 0.1) * 0.5 + 0.5;
  
  // Blend between Mandelbrot (beginning) and a transcendent form
  vec2 z = vec2(0.0);
  vec2 c = uv * 2.5 / uZoom;
  
  // Apply gentle rotation based on cycle and device tilt
  float tiltRotation = uDeviceTilt.x * 0.5;
  c = rotate2D(cycle * PI * 0.25 + tiltRotation) * c;
  c += uDeviceTilt * 0.2; // Subtle position influence
  
  // Offset toward the edge of infinity
  c += vec2(-0.5, 0.0);
  
  float iterations = 0.0;
  float phase = 0.0;
  float trap1 = 1e10;
  float trap2 = 1e10;
  
  for (int i = 0; i < MAX_ITERATIONS; i++) {
    if (dot(z, z) > 256.0) break;
    
    // Track the angle for phase coloring
    phase += atan(z.y, z.x);
    
    // Standard Mandelbrot iteration
    float xTemp = z.x * z.x - z.y * z.y + c.x;
    z.y = 2.0 * z.x * z.y + c.y;
    z.x = xTemp;
    
    // Multiple orbit traps for rich visualization
    trap1 = min(trap1, abs(z.x));
    trap2 = min(trap2, abs(z.y));
    
    iterations += 1.0;
  }
  
  float t = iterations / float(MAX_ITERATIONS);
  
  // Smooth the iteration count
  if (t < 1.0) {
    float log_zn = log(dot(z, z)) / 2.0;
    float nu = log(log_zn / log(2.0)) / log(2.0);
    t = (iterations + 1.0 - nu) / float(MAX_ITERATIONS);
  }
  
  // Normalize traps
  float trapBlend = exp(-trap1 * 5.0) + exp(-trap2 * 5.0);
  
  vec3 col;
  
  if (t >= 1.0) {
    // Inside the set - the point on the edge, the self
    col = uColorPrimary * 0.3;
    
    // Pulsing with breath/life
    float breathe = 0.5 + 0.5 * sin(uTime * 0.5);
    col += uColorSecondary * breathe * 0.2;
    
    // Audio creates inner resonance
    col += uColorAccent * uBass * 0.3;
  } else {
    // The infinite space around
    
    // Layer 1: Base iteration coloring
    float hue1 = t * 3.0 + uTime * 0.05;
    vec3 layer1 = mix(uColorPrimary, uColorSecondary, sin(hue1) * 0.5 + 0.5);
    
    // Layer 2: Phase-based spiral patterns
    float phaseColor = sin(phase * 0.1 + uTime * 0.2) * 0.5 + 0.5;
    vec3 layer2 = mix(uColorSecondary, uColorAccent, phaseColor);
    
    // Layer 3: Orbit trap glow
    vec3 layer3 = uColorAccent * trapBlend;
    
    // Blend all layers
    col = layer1 * 0.5 + layer2 * 0.3 + layer3 * 0.4;
    
    // The edge between same and different
    float edgeGlow = 1.0 - smoothstep(0.0, 0.1, t);
    col += uColorAccent * edgeGlow * 0.5;
  }
  
  // "Everything is the same, everything is different"
  // Subtle shimmer showing both states
  float shimmer = sin(uTime * 2.0 + length(uv) * 10.0) * 0.05;
  col += vec3(shimmer) * uHigh;
  
  // The cyclical return - colors shift toward the beginning
  col = mix(col, uColorAccent * 0.5, cycle * 0.1);
  
  // Audio-reactive breathing (stronger)
  float breath = 1.0 + uAudioAverage * 0.5 + uBass * 0.3;
  col *= breath;
  col += uColorAccent * uHigh * 0.2;
  
  // Centered radial light - the point of consciousness
  float centerLight = exp(-length(uv) * 2.0);
  col += uColorAccent * centerLight * 0.3 * (1.0 + uBass * 0.5);
  
  // Very subtle grain for texture
  float grain = fract(sin(dot(uv + uTime * 0.001, vec2(12.9898, 78.233))) * 43758.5453);
  col += (grain - 0.5) * 0.015;
  
  // Soft vignette
  vec2 vigUv = vUv * 2.0 - 1.0;
  float vignette = 1.0 - dot(vigUv, vigUv) * 0.25;
  col *= vignette;
  
  // Final gamma
  col = pow(col, vec3(0.9));
  
  gl_FragColor = vec4(col, 1.0);
}

